/**
 * `<KbSourceForm>` — add a knowledge source (website URL, file upload, or
 * pasted text) to the workspace's default knowledge base. Reuses the knowledge
 * REST surface; creates a default KB on first use if the workspace has none.
 *
 * Upload flow: create source → PUT file to `presigned_put_url` → finalize.
 *
 * The create/finalize calls go through the shared `apiRequest` directly (rather
 * than the per-kbId hooks) because the KB id isn't known until the find-or-
 * create step runs inside submit; on success we invalidate the overview so the
 * new source appears with its `connecting` status.
 */

import { useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Tabs,
  TabsList,
  TabsTrigger,
  Textarea,
  toast,
} from "@cometchat-ai/ui-dashboard";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { apiRequest } from "../../agents/api/client";
import { formatApiError } from "../../lib/format-error";
import { useKnowledgeBases } from "../../knowledge/api/knowledge-bases";
import { isUploadCreateResponse } from "../../knowledge/api/types";
import type {
  CreateSourceRequest,
  CreateSourceResponse,
  KnowledgeBase,
  UploadCreateResponse,
} from "../../knowledge/api/types";
import { onboardingKeys } from "../api/onboarding";

type Mode = "url" | "upload" | "paste";

type UploadContentType = Extract<CreateSourceRequest, { source_type: "upload" }>["content_type"];

const CONTENT_TYPES: Record<string, UploadContentType> = {
  pdf: "application/pdf",
  txt: "text/plain",
  md: "text/markdown",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
};

function contentTypeFor(file: File): UploadContentType {
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  return CONTENT_TYPES[ext] ?? "text/plain";
}

export function KbSourceForm({
  slug,
  open,
  onOpenChange,
}: {
  slug: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation("onboarding");
  const qc = useQueryClient();

  const kbs = useKnowledgeBases(slug);
  const defaultKbId = kbs.data?.items?.[0]?.id;

  const [mode, setMode] = useState<Mode>("url");
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  function reset() {
    setName("");
    setUrl("");
    setText("");
    setFile(null);
    setMode("url");
  }

  async function ensureKbId(): Promise<string> {
    if (defaultKbId) return defaultKbId;
    const created = await apiRequest<KnowledgeBase>("POST", "/api/knowledge-bases", {
      name: "Knowledge base",
    });
    void qc.invalidateQueries({ queryKey: ["kb", slug] });
    return created.id;
  }

  function buildRequest(): CreateSourceRequest | null {
    const trimmedName = name.trim();
    if (mode === "url") {
      if (!url.trim()) return null;
      return { source_type: "url", name: trimmedName || url.trim(), url: url.trim() };
    }
    if (mode === "paste") {
      if (!text.trim()) return null;
      return { source_type: "paste", name: trimmedName || "Pasted text", text };
    }
    if (file) {
      return {
        source_type: "upload",
        name: trimmedName || file.name,
        filename: file.name,
        content_type: contentTypeFor(file),
        byte_size: file.size,
      };
    }
    return null;
  }

  const valid =
    (mode === "url" && url.trim() !== "") ||
    (mode === "paste" && text.trim() !== "") ||
    (mode === "upload" && file !== null);

  async function uploadAndFinalize(kbId: string, created: UploadCreateResponse, theFile: File) {
    const put = await fetch(created.presigned_put_url, {
      method: "PUT",
      body: theFile,
      headers: { "Content-Type": theFile.type || "application/octet-stream" },
    });
    if (!put.ok) throw new Error(t("kb.uploadError"));
    await apiRequest("POST", `/api/knowledge-bases/${kbId}/sources/${created.source_id}/finalize`);
  }

  async function submit() {
    const req = buildRequest();
    if (!req) return;
    setBusy(true);
    try {
      const kbId = await ensureKbId();
      const created = await apiRequest<CreateSourceResponse>(
        "POST",
        `/api/knowledge-bases/${kbId}/sources`,
        req,
      );
      if (req.source_type === "upload" && isUploadCreateResponse(created) && file) {
        await uploadAndFinalize(kbId, created, file);
      }
      void qc.invalidateQueries({ queryKey: ["kb", slug] });
      void qc.invalidateQueries({ queryKey: onboardingKeys.overview(slug) });
      toast.success(t("kb.createdToast"));
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent data-testid="onboarding-kb-form">
        <DialogHeader>
          <DialogTitle>{t("kb.title")}</DialogTitle>
          <DialogDescription>{t("kb.description")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Tabs value={mode} onValueChange={(v) => setMode(v as Mode)}>
            <TabsList>
              <TabsTrigger value="url">{t("kb.tabs.url")}</TabsTrigger>
              <TabsTrigger value="upload">{t("kb.tabs.upload")}</TabsTrigger>
              <TabsTrigger value="paste">{t("kb.tabs.paste")}</TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="space-y-1">
            <Label htmlFor="kb-name">{t("kb.nameLabel")}</Label>
            <Input
              id="kb-name"
              value={name}
              placeholder={t("kb.namePlaceholder")}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          {mode === "url" ? (
            <div className="space-y-1">
              <Label htmlFor="kb-url">{t("kb.urlLabel")}</Label>
              <Input
                id="kb-url"
                type="url"
                value={url}
                placeholder={t("kb.urlPlaceholder")}
                onChange={(e) => setUrl(e.target.value)}
                data-testid="onboarding-kb-url"
              />
            </div>
          ) : null}

          {mode === "upload" ? (
            <div className="space-y-1">
              <Label htmlFor="kb-file">{t("kb.fileLabel")}</Label>
              <Input
                id="kb-file"
                type="file"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                data-testid="onboarding-kb-file"
              />
            </div>
          ) : null}

          {mode === "paste" ? (
            <div className="space-y-1">
              <Label htmlFor="kb-text">{t("kb.textLabel")}</Label>
              <Textarea
                id="kb-text"
                rows={6}
                value={text}
                placeholder={t("kb.textPlaceholder")}
                onChange={(e) => setText(e.target.value)}
                data-testid="onboarding-kb-text"
              />
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            {t("common.cancel")}
          </Button>
          <Button onClick={submit} disabled={!valid || busy} data-testid="onboarding-kb-submit">
            {busy ? t("kb.submitting") : t("kb.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
